import React from "react"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import SearchInput from "./SearchInput"
import { COLORS } from "../../utils"
import makeStyles from "@mui/styles/makeStyles"
import IconButton from "@mui/material/IconButton"
import RefreshIcon from "@mui/icons-material/Refresh"
import clsx from "clsx"
import ProductPlacementIcon from "@mui/icons-material/Kitchen"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import { FRIDGE_FILTERS } from "../../utils/inventoryUtils"

const useStyles = makeStyles(() => ({
  searchFieldDiv: {
    display: "flex",
    alignItems: "center",
    padding: "14px 18px 14px 10px",
    width: "100%",
    backgroundColor: COLORS.LIGHT_GREY,
  },
  // The search field is fixed, so it leaves the flow and this row reserves no height for it.
  // On the reception screen its siblings hold the row open; on the inventory it is alone, and
  // the fridge pills slide up underneath it. 45px is the field's own height.
  searchFieldDivAlone: {
    minHeight: 45 + 14 + 14,
  },
  selectType: {
    width: 158,
    color: COLORS.DARK_GREY,
    marginLeft: 395,
    "&.is-tablet": {
      marginLeft: 522,
    },
  },
  actionContainer: {
    marginLeft: "auto",
  },
  icon: {
    "& svg": {
      fontSize: 30,
    },
  },
  // KFC-352: the fridge filter sits on its own row under the search bar, as pills, which is
  // how the DSG-36 prototype lays it out.
  fridgeFilterRow: {
    display: "flex",
    // The prototype gives this row 12px above the pills; without it they sit flush against the
    // search field and the two rows read as one.
    padding: "12px 18px 14px 10px",
    backgroundColor: COLORS.LIGHT_GREY,
  },
  fridgeFilterGroup: {
    gap: 8,
    "& .MuiToggleButton-root": {
      border: `1px solid ${COLORS.DEFAULT_GREY}`,
      borderRadius: 999,
      padding: "6px 18px",
      textTransform: "none",
      fontSize: 15,
      color: COLORS.DARK_GREY,
      backgroundColor: COLORS.WHITE,
      "&.Mui-selected, &.Mui-selected:hover": {
        color: COLORS.WHITE,
        backgroundColor: COLORS.PRIMARY_COLOR,
        borderColor: COLORS.PRIMARY_COLOR,
      },
    },
  },
}))

const Filter = (props) => {
  const {
    isTablet,
    status,
    onStatusChange,
    onNameChange,
    isInventory,
    refresh,
    focusSearch,
    searchValue,
    sendProductPlacement,
    fridgeFilter,
    onFridgeFilterChange,
    hasRetailOffer,
  } = props
  const classes = useStyles()

  // KFC-352: hubs without a retail offer keep exactly the screen they had — one stock, no
  // filter.
  const showFridgeFilter = isInventory && hasRetailOffer

  return (
    <>
    <div className={clsx(classes.searchFieldDiv, { [classes.searchFieldDivAlone]: isInventory })}>
      <SearchInput
        defaultValue={searchValue}
        focused={focusSearch}
        isTablet={isTablet}
        onNameChange={onNameChange}
        isInventory={isInventory}
      />

      {!isInventory && (
        <Select
          variant="standard"
          className={clsx(classes.selectType, { "is-tablet": isTablet })}
          labelId="select-status"
          id="select-status"
          value={status}
          onChange={(e) => onStatusChange(e.target.value)}
          autoWidth={true}
        >
          <MenuItem value={"TODO"}>A réceptionner</MenuItem>
          <MenuItem value={"IN_PROGRESS"}>En cours</MenuItem>
          <MenuItem value={"DONE"}>Réceptionnés</MenuItem>
          <MenuItem value={"ALL"}>Tous les produits</MenuItem>
        </Select>
      )}

      {/*
        * KFC-352: the inventory screen drops both buttons, as the ticket asks. Note that this
        * removes two features from that screen, not just two icons: the product placement
        * e-mail and the manual refresh.
        */}
      {!isInventory && (
        <div className={classes.actionContainer}>
          {/* button to send on email product placement */}
          <IconButton
            className={classes.icon}
            onClick={sendProductPlacement}
            size="large"
          >
            <ProductPlacementIcon />
          </IconButton>
          {/* button to refresh */}
          <IconButton className={classes.icon} onClick={refresh} size="large">
            <RefreshIcon />
          </IconButton>
        </div>
      )}
    </div>

    {showFridgeFilter && (
      <div className={classes.fridgeFilterRow}>
        <ToggleButtonGroup
          className={classes.fridgeFilterGroup}
          value={fridgeFilter}
          exclusive
          onChange={(event, value) => {
            // A group with `exclusive` sends null when the active button is clicked again.
            // Ignoring it keeps one position always selected, as the prototype does.
            if (null !== value) onFridgeFilterChange(value)
          }}
        >
          <ToggleButton value={FRIDGE_FILTERS.ALL}>Tous</ToggleButton>
          <ToggleButton value={FRIDGE_FILTERS.RETAIL}>Libre-service</ToggleButton>
          <ToggleButton value={FRIDGE_FILTERS.CLICK_AND_COLLECT}>Click &amp; collect</ToggleButton>
        </ToggleButtonGroup>
      </div>
    )}
    </>
  )
}

export default Filter
